import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/data/videos";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const goSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur">
      <div className="container-page">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-sm font-extrabold text-background-50">
              91
            </span>
            <span className="font-heading text-lg font-extrabold text-foreground-950">
              免费视频网站91
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.slice(0, 1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive(link.to)
                    ? "font-semibold text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm transition-colors ${
                  location.pathname.startsWith("/category")
                    ? "font-semibold text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                分类
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    categoryOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {categoryOpen && (
                <div className="animate-fade-in absolute left-0 top-full mt-1 w-56 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-2">
                  {allCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive(link.to)
                    ? "font-semibold text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={goSearch} className="hidden flex-1 justify-end md:flex">
            <div className="relative w-full max-w-xs">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频、分类、标签"
                className="w-full rounded-md border border-background-200 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-800 md:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 md:hidden">
          <div className="container-page space-y-4 py-4">
            <form onSubmit={goSearch}>
              <div className="relative w-full">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索视频、分类、标签"
                  className="w-full rounded-md border border-background-200 bg-background-100 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </form>
            <div className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="cursor-pointer rounded-md px-3 py-3 text-sm text-foreground-800 hover:bg-background-100"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                视频分类
              </p>
              <div className="grid grid-cols-2 gap-2">
                {allCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex cursor-pointer items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-700"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={cat.icon}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}